import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AlertCircle, Loader2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { usePageEnter } from '@/hooks/useAppleMotion';

const LoginPage = () => {
  const { login, error, isAuthenticated, isLoading, checkAuth } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { className: pageEnterClass } = usePageEnter();

  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/';

  // On mount, re-check auth in case the user just completed the OAuth callback
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Redirect if already authenticated (skip on localhost — always mock-authenticated)
  useEffect(() => {
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    if (isAuthenticated && !isLoading && !isLocalhost) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate, from]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-[#0a0e0c] text-wayne-ink-3">
        <Loader2 size={28} className="animate-spin" />
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen bg-[#0a0e0c] flex flex-col items-center justify-center px-6 ${pageEnterClass}`}
    >
      {/* Radial glow behind title */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse at center, rgba(63,90,58,0.15) 0%, transparent 70%)',
        }}
      />

      <div className="relative z-10 flex flex-col items-center text-center max-w-[640px]">
        {/* Eyebrow */}
        <p
          className="text-wayne-ink-3 uppercase mb-8"
          style={{ fontSize: '10px', letterSpacing: '0.12em' }}
        >
          Welcome &middot; Onboarding
        </p>

        {/* Giant title */}
        <h1 className="font-serif font-medium text-[clamp(56px,10vw,100px)] leading-none mb-4">
          <span className="text-white">Wayne</span>{' '}
          <span className="text-wayne-accent italic">AI</span>
        </h1>

        {/* Tagline */}
        <p
          className="text-wayne-ink-3 uppercase mb-10"
          style={{ fontSize: '12px', letterSpacing: '0.15em' }}
        >
          Agents who{' '}
          <span className="text-wayne-accent">already</span>{' '}
          know you
        </p>

        {/* Description */}
        <p className="font-serif italic text-wayne-ink-2 text-[clamp(16px,2.5vw,20px)] leading-relaxed mb-4">
          A constellation of everything you have access to.
        </p>
        <p className="text-wayne-ink-3 text-[14px] leading-relaxed max-w-[560px] mb-12">
          Wayne pulls together your phonetool, bindles, AWS accounts, Taskei rooms,
          CTIs, oncall rotations, channels you live in, and a dozen other sources
          — so the agents have your context from minute one. Nothing leaves Amazon.
        </p>

        {/* Error state */}
        {error && (
          <div className="border border-wayne-hairline border-l-4 border-l-wayne-red bg-wayne-surface rounded-lg p-3 flex items-start gap-2 text-[13px] text-wayne-ink mb-8 w-full max-w-[400px]">
            <AlertCircle size={16} className="text-wayne-red mt-0.5 shrink-0" />
            <span>{error.message}</span>
          </div>
        )}

        {/* CTA Button */}
        <button
          type="button"
          onClick={login}
          className="bg-wayne-accent hover:bg-wayne-accent-2 text-white rounded-full px-10 py-4 text-[16px] font-medium transition-colors cursor-pointer"
          style={{ boxShadow: '0 0 30px rgba(63,90,58,0.3)' }}
        >
          Begin discovery &rarr;
        </button>

        {/* Footnote */}
        <p className="text-wayne-ink-3 text-[12px] mt-6">
          Takes about 18 seconds. You can re-run any time.
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
